import { useEffect, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import Page from '../../../components/Page';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  setCurrentPage,
  setFilters,
  setLimit,
  setSkip,
  setStatus,
} from '../../../store/partnerAuditLog/partnerAuditLogSlice';
import { findPartnerAuditLog } from '../../../store/partnerAuditLog/api';
import { findPartner } from '../../../store/partner/api';
import { Partner } from '../../../store/partner/api/interface';
import Loader from '../../../components/Loader';
import Label from '../../../components/Label';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import Scrollbar from '../../../components/Scrollbar';

const shortKeyId = (key?: string | null): string => {
  if (!key) {
    return '-';
  }
  const prefix = key.match(/^(adori_pk_(?:test|live)_)/);
  if (prefix) {
    return `${prefix[1]}....`;
  }
  return key.length > 16 ? `${key.slice(0, 14)}....` : key;
};

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    data,
    totalRecords,
    limit,
    currentPage,
    status,
    skip,
    isLoading,
    filters,
  } = useAppSelector((state) => state.partnerAuditLog);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [draft, setDraft] = useState(filters);

  useEffect(() => {
    dispatch(findPartner({ limit: 100, skip: 0 }))
      .unwrap()
      .then((result) => setPartners(result.data || []))
      .catch(() => setPartners([]));
  }, [dispatch]);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findPartnerAuditLog({
          limit,
          skip,
          partnerId: filters.partnerId || undefined,
          orderId: filters.orderId || undefined,
          from: filters.from || undefined,
          to: filters.to || undefined,
        })
      );
    }
  }, [dispatch, limit, skip, status, filters]);

  const handleApplyFilters = (): void => {
    dispatch(setFilters(draft));
  };

  const handleClearFilters = (): void => {
    const empty = { partnerId: '', orderId: '', from: '', to: '' };
    setDraft(empty);
    dispatch(setFilters(empty));
  };

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setStatus({ status: 'idle' }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  return (
    <Page title={'Partner Audit'}>
      <Container maxWidth={'xl'}>
        <HeaderBreadcrumbs
          heading={'Partner Audit'}
          links={[
            { name: 'Dashboard', href: '/' },
            { name: 'Partners', href: '/partners' },
          ]}
        />

        <Card sx={{ p: 2, mb: 3 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 2,
              alignItems: 'center',
            }}
          >
            <FormControl fullWidth size='small'>
              <InputLabel id='partner-audit-partner'>Partner</InputLabel>
              <Select
                labelId='partner-audit-partner'
                label='Partner'
                value={draft.partnerId}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    partnerId: String(e.target.value),
                  }))
                }
              >
                <MenuItem value=''>All partners</MenuItem>
                {partners.map((partner) => (
                  <MenuItem
                    key={partner.id}
                    value={partner.id}
                  >
                    {partner.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size='small'
              label='Order ID'
              value={draft.orderId}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, orderId: e.target.value.trim() }))
              }
            />
            <TextField
              size='small'
              label='From'
              type='date'
              InputLabelProps={{ shrink: true }}
              value={draft.from}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, from: e.target.value }))
              }
            />
            <TextField
              size='small'
              label='To'
              type='date'
              InputLabelProps={{ shrink: true }}
              value={draft.to}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, to: e.target.value }))
              }
            />
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              variant='contained'
              onClick={handleApplyFilters}
            >
              Apply
            </Button>
            <Button
              variant='outlined'
              onClick={handleClearFilters}
            >
              Clear
            </Button>
          </Box>
        </Card>

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 1100, position: 'relative' }}>
              <Table size={'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>When</TableCell>
                    <TableCell align='center'>Partner</TableCell>
                    <TableCell align='center'>Token</TableCell>
                    <TableCell align='center'>Action</TableCell>
                    <TableCell align='center'>Method / Path</TableCell>
                    <TableCell align='center'>Order</TableCell>
                    <TableCell align='center'>Result</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align='center'
                      >
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : data?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align='center'
                      >
                        No record found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align='center'>
                          {row.createdAt
                            ? new Date(row.createdAt).toLocaleString()
                            : '-'}
                        </TableCell>
                        <TableCell align='center'>
                          {row.partnerName || row.partnerId}
                        </TableCell>
                        <TableCell align='center'>
                          {shortKeyId(row.publicKey)}
                        </TableCell>
                        <TableCell align='center'>{row.action}</TableCell>
                        <TableCell align='center'>
                          {row.method} {row.path}
                        </TableCell>
                        <TableCell align='center'>
                          {row.orderId || '-'}
                        </TableCell>
                        <TableCell align='center'>
                          <Label
                            variant='ghost'
                            color={
                              row.result === 'success' ? 'success' : 'error'
                            }
                          >
                            {row.result === 'success'
                              ? 'success'
                              : row.errorCode || row.result}
                          </Label>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component='div'
              count={totalRecords}
              rowsPerPage={limit}
              page={Math.min(
                Math.max(0, Math.ceil(totalRecords / limit) - 1),
                currentPage
              )}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
};

export default Index;
